import {
  useBlockOrUnblockContactMutation,
  useDeleteContactsMutation,
  useDeleteUnusedContactsMutation,
} from '@/types/generated/graphql';
import { notification } from 'antd';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { DeleteContactsType, FormValues } from '../contacts.types';
import {
  cacheBlockContactV2,
  cacheMarkBlockUnblockMessage,
  cacheUpdateBlockUnBlockContact,
  cacheUpdateRecentMessageUpdatedAt,
} from '@/graphql/cache/cache-mutations';
import { useProfile } from '@components/profile-dropdown/hooks/use-profile-dropdown';
import { useModal } from '@shared/hooks/use-modal';

export const useDeleteBlockContacts = ({
  selectedRowKeys = [],
  setSelectedRowKeys,
  onAfterAction,
  channelId,
  clientId,
}: DeleteContactsType) => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openBlockModal, setOpenBlockModal] = useState(false);
  const { userName } = useProfile();
  const { open, handleClose, handleOpen } = useModal();

  const { control, watch, reset } = useForm<FormValues>({
    defaultValues: { note: '' },
  });

  const toggleDeleteModal = () => {
    setOpenDeleteModal((prev) => !prev);
  };

  const toggleBlockModal = () => {
    setOpenBlockModal((prev) => !prev);
  };

  const [deleteContacts, { loading: deleteContactsLoading }] = useDeleteContactsMutation({
    onCompleted: (data) => {
      notification.success({
        message: data?.deleteContacts?.message,
      });
      toggleDeleteModal();
      setSelectedRowKeys?.([]);
      onAfterAction?.();
    },
    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
    update: (cache) => {
      cache.modify({
        fields: {
          contactsV2: () => {},
        },
      });
    },
  });

  const confirmDelete = () => {
    deleteContacts({
      variables: { ids: selectedRowKeys?.map(Number) },
    });
  };

  const [deleteContact, { loading: deleteContactLoading }] = useDeleteUnusedContactsMutation({
    onCompleted: (data) => {
      notification.success({
        message: data?.deleteUnusedContacts?.message,
      });
      toggleDeleteModal();
      onAfterAction?.();
    },
    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
    update: (cache) => {
      cache.modify({
        fields: {
          contactsV2: () => {},
          getContactsCount: () => {},
        },
      });
    },
  });

  const confirmDeleteContactDetail = (selectedContactId: number) => {
    deleteContact({
      variables: {
        input: {
          contactIds: [selectedContactId],
        },
      },
    });
  };

  const [blockContact, { loading: blockContactLoading }] = useBlockOrUnblockContactMutation({
    onCompleted: (data, clientOptions) => {
      notification.success({
        message: data?.blockOrUnblockContact?.message,
      });
      setOpenBlockModal(false);
      onAfterAction?.();
      reset();
      const isBlocked = !!data?.blockOrUnblockContact?.data?.isBlocked;
      if (isBlocked) {
        cacheBlockContactV2(
          clientOptions?.variables?.blockOrUnblockContactId,
          clientOptions?.variables?.input?.blockReason,
          String(userName)
        );
      }
      cacheUpdateBlockUnBlockContact(data?.blockOrUnblockContact?.data?.id ?? 0, isBlocked);
      if (channelId && clientId) {
        cacheMarkBlockUnblockMessage({ clientId, channelId, isBlocked });
        cacheUpdateRecentMessageUpdatedAt(clientId, channelId);
      }
    },
    onError: (error) => {
      notification.error({
        message: error?.message,
      });
    },
  });

  const confirmBlock = (selectedContactId: number, blockReason: string) => {
    blockContact({
      variables: {
        blockOrUnblockContactId: selectedContactId,
        input: {
          isBlocked: true,
          blockReason,
        },
      },
    });
  };
  const unblockContact = (selectedContactId: number) => {
    blockContact({
      variables: {
        blockOrUnblockContactId: selectedContactId,
        input: {
          isBlocked: false,
        },
      },
    });
  };

  const handleBlockConfirm = (selectedContactId: number) => {
    const note = watch('note')?.trim();
    if (!note) return;

    confirmBlock(selectedContactId, note);
  };

  return {
    openDeleteModal,
    openBlockModal,
    toggleDeleteModal,
    toggleBlockModal,
    confirmDelete,
    confirmDeleteContactDetail,
    confirmBlock,
    blockContactLoading,
    deleteContactsLoading,
    deleteContactLoading,
    handleBlockConfirm,
    blockControl: control,
    blockWatch: watch,
    handleOpenUnblockModal: handleOpen,
    openUnblockModal: open,
    handleCloseUnblockModal: handleClose,
    unblockContact,
  };
};
